import { Controller } from '@hotwired/stimulus'
import { i18n } from 'config/translations'

// 面談日時をブラウザのタイムゾーンに変換して表示する
// 対象要素の data-meeting-date / data-start-time / data-end-time / data-facility-timezone を読み取り、
// ブラウザ TZ に変換した日時で textContent を書き換える。
// ブラウザ TZ と施設 TZ が同一の場合は要素を非表示にする。
export default class extends Controller {
  static targets = ['browserTime']

  connect() {
    this.displayBrowserTimezones()
  }

  displayBrowserTimezones() {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone

    this.browserTimeTargets.forEach((target) => {
      const facilityTz = target.dataset.facilityTimezone
      if (!facilityTz) return

      if (browserTz === facilityTz) {
        target.style.display = 'none'
        return
      }

      const dateStr = target.dataset.meetingDate
      const startTime = target.dataset.startTime
      const endTime = target.dataset.endTime

      const startConverted = this.convertTimezone(dateStr, startTime, facilityTz, browserTz)
      const endConverted = this.convertTimezone(dateStr, endTime, facilityTz, browserTz)

      const tzAbbr = this.getTimezoneAbbr(startConverted.utcDate, browserTz)
      const formattedDate = this.formatDate(startConverted.utcDate, browserTz)

      const separator = i18n.locale === 'ja' ? '~' : ' - '
      target.textContent = `${formattedDate}, ${startConverted.time}${separator}${endConverted.time}  ${tzAbbr} (Your time)`
    })
  }

  // 施設 TZ の時刻をブラウザ TZ に変換する
  convertTimezone(dateStr, timeStr, fromTz, toTz) {
    // 日付・時刻を UTC として仮パースし、施設 TZ のオフセットで補正して実際の UTC を求める
    const naiveUtc = new Date(`${dateStr}T${timeStr}:00.000Z`)
    const fromOffset = this.getTimezoneOffsetMs(naiveUtc, fromTz)
    const utcDate = new Date(naiveUtc.getTime() + fromOffset)

    // [issue#2756] 英語は12時間表記（10:00 AM）。en-GB は 'am' と小文字になるため en-US を使う。
    // 日本語は従来どおり24時間表記のまま変えない。
    const ja = i18n.locale === 'ja'
    const time = utcDate.toLocaleTimeString(ja ? 'en-GB' : 'en-US', {
      timeZone: toTz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: !ja,
    })

    return { utcDate, time }
  }

  // 指定日時における TZ の UTC オフセット（ミリ秒）を返す
  getTimezoneOffsetMs(date, tz) {
    const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' })
    const tzStr = date.toLocaleString('en-US', { timeZone: tz })
    return new Date(utcStr).getTime() - new Date(tzStr).getTime()
  }

  formatDate(date, tz) {
    const dateStr = date.toLocaleDateString('en-CA', { timeZone: tz })
    const weekday = date.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' })
    return `${dateStr}, ${weekday}`
  }

  getTimezoneAbbr(date, tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(date)
    const tzPart = parts.find((p) => p.type === 'timeZoneName')
    return tzPart ? tzPart.value : tz
  }
}
